import { EApplicantType, ECreditApplicationStatus, EDecision, EDocumentStatus, EDocumentType } from '@if/core/types'
import { ErrorAlert } from '@if/mui'
import { Button, Card, CardActions, CardContent, LinearProgress, Typography } from '@mui/material'
import { useBypassVouched, useResetVouched } from '@src/data/api/credit-application/credit-application-api'
import { CreditApplication, User, useDevTools } from '@src/data/types'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { reportErrorToConsole } from '@src/services/error-logger'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  creditApplication: CreditApplication
  applicantType: EApplicantType
  user: User | null
  editDisabled: boolean
  // B2C runs Vouched inside the Draft soft-hit flow → only enable the reset while still Draft.
  // Personal runs Vouched after approval (thin-file payout gate) → enable post-Draft + Approved,
  // following the same shape as the Flinks reset (see FlinksActionsSection).
  requireDraftOnly?: boolean
}

const VouchedSection = ({ creditApplication, applicantType, user, editDisabled, requireDraftOnly = false }: Props) => {
  const { t } = useTranslation()
  const [resetVouchedHook, resetting] = useResetVouched()
  const [bypassVouchedHook, bypassing, error] = useBypassVouched()
  const { documents } = useCreditApplicationData();

  const loading = resetting || bypassing

  const resetVouched = () => {
    resetVouchedHook({
      creditApplicationId: creditApplication.id,
      financingProgramId: creditApplication.financingProgramId,
      applicantType,
    }).catch(reportErrorToConsole)
  }

  const bypassVouched = (outcome: 'success' | 'failure') => {
    bypassVouchedHook({
      creditApplicationId: creditApplication.id,
      financingProgramId: creditApplication.financingProgramId,
      applicantType,
      outcome,
    }).catch(reportErrorToConsole)
  }

  const isDraft = creditApplication.status === ECreditApplicationStatus.Draft
  const isApproved = creditApplication.finalCreditDecision?.decision === EDecision.Approved

  const disabled = requireDraftOnly
    ? !isDraft || loading
    : !user?.rights.canEditCreditApp || isDraft || !isApproved || editDisabled || loading

  // Once Vouched has already succeeded for this applicant, the bypass buttons are no longer
  // useful — disable them regardless of the Resend Vouched Link state above.
  const vouchedAlreadySucceeded = documents.some(
    (d) => d.applicantType === applicantType && d.typeId === EDocumentType.ValidPhotoId && d.status === EDocumentStatus.Approved,
  )
  const bypassDisabled = disabled || vouchedAlreadySucceeded

  const titleKey = applicantType === EApplicantType.CoApplicant ? 'credit.vouched.titleCoApplicant' : 'credit.vouched.title'

  return (
    <Card elevation={3} sx={{ mt: 3, mb: 3 }}>
      {loading && <LinearProgress />}
      <CardContent>
        <Typography component="div" variant="h5">
          {t(titleKey)}
        </Typography>
      </CardContent>
      <CardActions>
        <Button disabled={disabled} onClick={resetVouched}>
          {t('credit.vouched.reset')}
        </Button>
        {useDevTools && (
          <>
            <Button variant="outlined" color="success" disabled={bypassDisabled} onClick={() => bypassVouched('success')}>
              {t('credit.vouched.bypassSuccess')}
            </Button>
            <Button variant="outlined" color="error" disabled={bypassDisabled} onClick={() => bypassVouched('failure')}>
              {t('credit.vouched.bypassFailure')}
            </Button>
          </>
        )}
      </CardActions>
      {error && (
        <CardContent sx={{ pt: 0 }}>
          <ErrorAlert error={error} />
        </CardContent>
      )}
    </Card>
  )
}

export default memo(VouchedSection)
